import { Section } from '@web_opxp/components';
import { ConfigSelectors, getWorkoutId, LanguageSelectors, StatsSelectors, useTranslation } from '@web_opxp/features';
import React from 'react';
import { connect } from 'react-redux';
import { DEFAULT_CATEGORY_ID } from '../base/constants';
import { EExerciseCategory } from '../base/types';
import { makeExerciseCategoryStatString } from '../base/utils';
import { IState } from '../store/state';
import ExercisePreferenceCheckbox from './ExercisePreferenceCheckbox';
import './ExerciseSection.scss';
import { PersonalizationActions } from './slice';

export interface IExercisePreference {
  categoryGroupId: number;
  exerciseId: string;
  workoutId: number;
  workoutEnabled: boolean;
}

interface IExerciseSectionOwnProps {
  userId?: number;
}

export interface IExerciseSectionProps extends IExerciseSectionOwnProps {
  loaded: boolean;
  data: IExercisePreference[];
  onSaveWorkoutStat: typeof PersonalizationActions.savePersonalizationWorkoutStat;
}

export const ExerciseSectionFC: React.FC<IExerciseSectionProps> = ({ loaded, data, userId, onSaveWorkoutStat }) => {
  const { t } = useTranslation();
  const saveWorkoutStatHandler = (workoutId: number, workoutEnabled: boolean, disableImpairment?: boolean) =>
    onSaveWorkoutStat(workoutId, workoutEnabled, disableImpairment, userId);
  return (
    <Section className="exercise-section">
      <div className="row">
        <div className="title col-4">
          <h3>{t('Personalization.ExerciseSection.Title')}</h3>
        </div>
        <div className="content col-8">
          <p>{t('Personalization.ExerciseSection.Description')}</p>
        </div>
      </div>
      <div className="row">
        <div className="col-4" />
        <div className="col-8">
          <div className="row">
            {loaded &&
              data.map((preference) => (
                <ExercisePreferenceCheckbox
                  key={`${preference.exerciseId}-${preference.workoutId}`}
                  preference={preference}
                  onSaveWorkoutStat={saveWorkoutStatHandler}
                />
              ))}
          </div>
        </div>
      </div>
    </Section>
  );
};

export const mapStateToProps = (state: IState, ownProps: IExerciseSectionOwnProps) => {
  const { userId } = ownProps;
  const langLoaded = !LanguageSelectors.isLoading(state);
  const statsLoaded = !StatsSelectors.isStatsLoading(state);
  const configLoaded = !ConfigSelectors.isConfigLoading(state);
  const loaded = statsLoaded && configLoaded && langLoaded;
  const data: IExercisePreference[] = [];
  if (loaded) {
    const exerciseIds = ConfigSelectors.getExerciseIds(state);
    for (const exerciseId of exerciseIds) {
      const categoryGroupId = ConfigSelectors.getCategoryGroupId(state, exerciseId);
      if (categoryGroupId === undefined) continue;

      const categoryIdStat: EExerciseCategory | undefined = StatsSelectors.getStatIntValueById(
        state,
        makeExerciseCategoryStatString(categoryGroupId),
        userId
      );
      // if stat is not set use default category (normal difficulty)
      const categoryId = categoryIdStat ?? DEFAULT_CATEGORY_ID;
      const workoutId = getWorkoutId(categoryId, categoryGroupId);

      const workoutEnabled = !StatsSelectors.getStatBooleanValueById(state, `9-4-${workoutId}`, userId);
      data.push({
        exerciseId,
        categoryGroupId,
        workoutId,
        workoutEnabled,
      });
    }
  }
  return {
    loaded,
    data,
  };
};

const mapDispatchToProps = {
  onSaveWorkoutStat: PersonalizationActions.savePersonalizationWorkoutStat,
};

const ExerciseSection = connect(mapStateToProps, mapDispatchToProps)(ExerciseSectionFC);

export default ExerciseSection;
